"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useCallback, useId } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { DialogStackContext, isAboveDialogs, isTopDialog, topDialogElement, useDialogStack } from "@/hooks/useDialogStack";
import { useScrollLock } from "@/hooks/useScrollLock";
import { X } from "lucide-react";
type ModalSize = "sm" | "md" | "lg" | "xl";
interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  size?: ModalSize;
  closeOnBackdrop?: boolean;
  closeOnEscape?: boolean;
  children: React.ReactNode;
  className?: string;
  /**
   * Vertical placement of the modal card. "bottom" makes it a bottom-anchored
   * sheet on small screens (mobile) while staying a centered card on `sm+`.
   * Defaults to "center" so existing consumers are unaffected.
   */
  align?: "center" | "bottom";
}
const sizeStyles: Record<ModalSize, string> = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-2xl",
  xl: "max-w-4xl"
};

// The stops of the dialog's tab order. An element taken out of it (tabindex="-1", the inactive
// tab of a Tabs) is none, and an iframe is one (DealEmailPreviewDialog's email): the trap wraps
// from the last stop there is. Pinned by DealEmailPreviewButton.test.tsx.
const FOCUSABLE_SELECTOR = ["a[href]", "button:not([disabled])", "textarea:not([disabled])", "input:not([disabled])", "select:not([disabled])", "iframe", "[tabindex]"].map(candidate => `${candidate}:not([tabindex="-1"])`).join(", ");

/** Focuses the first control in a dialog, or the dialog itself. */
function focusFirst(dialog: HTMLElement) {
  const focusable = dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
  if (focusable.length > 0) {
    focusable[0].focus();
  } else {
    dialog.focus();
  }
}

/**
 * Modal - Reusable modal dialog component
 *
 * Renders via portal to document.body with backdrop overlay.
 * Supports escape key close, backdrop click close, scroll lock,
 * and focus trapping for accessibility.
 *
 * Sizes:
 * - sm: Compact dialogs (max-w-sm)
 * - md: Standard dialogs (max-w-lg)
 * - lg: Content-heavy dialogs (max-w-2xl)
 * - xl: Large content like forms or previews (max-w-4xl)
 */
export function Modal(t0) {
  const $ = _c(45);
  const {
    open,
    onClose,
    title,
    description,
    size: t1,
    closeOnBackdrop: t2,
    closeOnEscape: t3,
    children,
    className,
    align: t4
  } = t0;
  const size = t1 === undefined ? "md" : t1;
  const closeOnBackdrop = t2 === undefined ? true : t2;
  const closeOnEscape = t3 === undefined ? true : t3;
  const align = t4 === undefined ? "center" : t4;
  const contentRef = useRef(null);
  const uniqueId = useId();
  const titleId = `modal-title-${uniqueId}`;
  const descriptionId = `modal-description-${uniqueId}`;
  const stacked = useDialogStack(open, contentRef);
  useScrollLock(open);
  let t5;
  let t6;
  if ($[0] !== closeOnEscape || $[1] !== onClose || $[2] !== open || $[3] !== stacked) {
    t5 = () => {
      if (!open || !closeOnEscape) {
        return;
      }
      const handleKeyDown = e => {
        if (e.key !== "Escape" || e.defaultPrevented || !isTopDialog(stacked)) {
          return;
        }
        if (isAboveDialogs(e.target)) {
          return;
        }
        e.preventDefault();
        onClose();
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    };
    t6 = [open, closeOnEscape, onClose, stacked];
    $[0] = closeOnEscape;
    $[1] = onClose;
    $[2] = open;
    $[3] = stacked;
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[6] !== open || $[7] !== stacked) {
    t7 = () => {
      if (!open) {
        return;
      }
      const previousFocus = document.activeElement as HTMLElement | null;
      const dialog = contentRef.current;
      const timer = setTimeout(() => {
        if (dialog && isTopDialog(stacked)) {
          focusFirst(dialog);
        }
      }, 0);
      return () => {
        clearTimeout(timer);
        if (document.activeElement !== document.body && !dialog?.contains(document.activeElement)) {
          return;
        }
        const below = topDialogElement();
        if (below?.isConnected && !below.contains(previousFocus)) {
          focusFirst(below);
        } else {
          if (previousFocus?.isConnected) {
            previousFocus.focus({
              preventScroll: true
            });
          }
        }
      };
    };
    t8 = [open, stacked];
    $[6] = open;
    $[7] = stacked;
    $[8] = t7;
    $[9] = t8;
  } else {
    t7 = $[8];
    t8 = $[9];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[10] !== open || $[11] !== stacked) {
    t9 = () => {
      if (!open) {
        return;
      }
      const handleFocusIn = e_0 => {
        const dialog_0 = contentRef.current;
        if (!dialog_0?.isConnected || dialog_0.contains(e_0.target as Node) || !isTopDialog(stacked)) {
          return;
        }
        if (isAboveDialogs(e_0.target)) {
          return;
        }
        dialog_0.focus();
      };
      document.addEventListener("focusin", handleFocusIn);
      return () => document.removeEventListener("focusin", handleFocusIn);
    };
    t10 = [open, stacked];
    $[10] = open;
    $[11] = stacked;
    $[12] = t10;
    $[13] = t9;
  } else {
    t10 = $[12];
    t9 = $[13];
  }
  useEffect(t9, t10);
  let t11;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = e_1 => {
      const dialog_1 = contentRef.current;
      if (e_1.key !== "Tab" || !dialog_1 || !dialog_1.contains(e_1.target as Node)) {
        return;
      }
      const focusable = dialog_1.querySelectorAll(FOCUSABLE_SELECTOR);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const onDialog = document.activeElement === dialog_1;
      if (!first) {
        e_1.preventDefault();
        dialog_1.focus();
      } else {
        if (e_1.shiftKey && (onDialog || document.activeElement === first)) {
          e_1.preventDefault();
          last.focus();
        } else {
          if (!e_1.shiftKey && (onDialog || document.activeElement === last)) {
            e_1.preventDefault();
            first.focus();
          }
        }
      }
    };
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const handleKeyDown_0 = t11;
  let t12;
  if ($[15] !== closeOnBackdrop || $[16] !== onClose) {
    t12 = e_2 => {
      if (closeOnBackdrop && e_2.target === e_2.currentTarget) {
        onClose();
      }
    };
    $[15] = closeOnBackdrop;
    $[16] = onClose;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const handleBackdropClick = t12;
  if (!open) {
    return null;
  }
  const t13 = align === "bottom" ? "items-end sm:items-center" : "items-center";
  let t14;
  if ($[18] !== t13) {
    t14 = cn("fixed inset-0 z-50 flex justify-center bg-black/50 animate-in fade-in duration-200", t13);
    $[18] = t13;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  const t15 = title ? titleId : undefined;
  const t16 = description ? descriptionId : undefined;
  const t17 = sizeStyles[size];
  let t18;
  if ($[20] !== className || $[21] !== t17) {
    t18 = cn("relative w-full mx-4 max-h-[90vh] bg-white rounded-2xl shadow-xl flex flex-col animate-in zoom-in-95 fade-in duration-200", t17, className);
    $[20] = className;
    $[21] = t17;
    $[22] = t18;
  } else {
    t18 = $[22];
  }
  let t19;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <X className="w-5 h-5" />;
    $[23] = t19;
  } else {
    t19 = $[23];
  }
  let t20;
  if ($[24] !== onClose) {
    t20 = <button onClick={onClose} className="absolute right-4 top-4 z-10 p-2 text-foreground-muted hover:text-foreground-muted hover:bg-surface-container-low rounded-lg transition-colors" aria-label={"Schlie\xDFen"}>{t19}</button>;
    $[24] = onClose;
    $[25] = t20;
  } else {
    t20 = $[25];
  }
  let t21;
  if ($[26] !== description || $[27] !== descriptionId || $[28] !== title || $[29] !== titleId) {
    t21 = (title || description) && <div className="px-6 pt-6 pb-0 pr-12">{title && <h2 id={titleId} className="text-xl font-bold text-primary">{title}</h2>}{description && <p id={descriptionId} className="text-sm text-foreground-muted mt-1">{description}</p>}</div>;
    $[26] = description;
    $[27] = descriptionId;
    $[28] = title;
    $[29] = titleId;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  let t22;
  if ($[31] !== children || $[32] !== stacked) {
    t22 = <DialogStackContext.Provider value={stacked}>{children}</DialogStackContext.Provider>;
    $[31] = children;
    $[32] = stacked;
    $[33] = t22;
  } else {
    t22 = $[33];
  }
  let t23;
  if ($[34] !== t15 || $[35] !== t16 || $[36] !== t18 || $[37] !== t20 || $[38] !== t21 || $[39] !== t22) {
    t23 = <div ref={contentRef} role="dialog" aria-modal="true" aria-labelledby={t15} aria-describedby={t16} tabIndex={-1} className={t18}>{t20}{t21}{t22}</div>;
    $[34] = t15;
    $[35] = t16;
    $[36] = t18;
    $[37] = t20;
    $[38] = t21;
    $[39] = t22;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== handleBackdropClick || $[42] !== t14 || $[43] !== t23) {
    t24 = createPortal(<div className={t14} onClick={handleBackdropClick} onKeyDown={handleKeyDown_0}>{t23}</div>, document.body);
    $[41] = handleBackdropClick;
    $[42] = t14;
    $[43] = t23;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  return t24;
}
interface ModalSectionProps {
  children: React.ReactNode;
  className?: string;
}

/**
 * ModalHeader - Header section inside a Modal
 * Use for custom header content beyond the built-in title/description
 */
export function ModalHeader(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("px-6 pt-6 pr-12", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

/**
 * ModalBody - Main scrollable content area of the Modal
 */
export function ModalBody(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex-1 overflow-y-auto px-6 py-4", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}

/**
 * ModalFooter - Footer section for actions (buttons, etc.)
 */
export function ModalFooter(t0) {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("px-6 py-4 border-t border-surface-container-low flex items-center justify-end gap-3", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <div className={t1}>{children}</div>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}